import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import UserCard from '../components/UserCard';
import Icon from '../components/Icon';
import './Discover.css';

function Discover() {
  const { user } = useAuth();
  const toast = useToast();
  const [golfers, setGolfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [radius, setRadius] = useState(25);
  const [handicapMin, setHandicapMin] = useState('');
  const [handicapMax, setHandicapMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [connectionStatuses, setConnectionStatuses] = useState({});

  const loadStatuses = useCallback(async (data, signal) => {
    if (!data.length) return;
    try {
      const statuses = await api.getConnectionStatusBatch(data.map(g => g.id));
      if (!signal.aborted) setConnectionStatuses(statuses);
    } catch (err) {
      if (!signal.aborted) console.error('Failed to load connection statuses:', err);
    }
  }, []);

  const loadNearbyGolfers = useCallback(async (signal) => {
    if (!user?.latitude || !user?.longitude) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.getNearbyUsers(
        user.latitude, user.longitude, radius,
        handicapMin || undefined, handicapMax || undefined
      );
      if (signal.aborted) return;
      setGolfers(data);
      await loadStatuses(data, signal);
    } catch (err) {
      if (!signal.aborted) console.error('Failed to load golfers:', err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [user, radius, handicapMin, handicapMax, loadStatuses]);

  const searchGolfers = useCallback(async (signal) => {
    if (!searchQuery || searchQuery.length < 2) {
      loadNearbyGolfers(signal);
      return;
    }
    setLoading(true);
    try {
      const data = await api.searchUsers(searchQuery);
      if (signal.aborted) return;
      setGolfers(data);
      await loadStatuses(data, signal);
    } catch (err) {
      if (!signal.aborted) console.error('Failed to search golfers:', err);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [searchQuery, loadNearbyGolfers, loadStatuses]);

  useEffect(() => {
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      if (searchQuery) {
        searchGolfers(controller.signal);
      } else {
        loadNearbyGolfers(controller.signal);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(debounce);
    };
  }, [searchQuery, searchGolfers, loadNearbyGolfers]);

  const handleConnect = async (userId) => {
    if (connectionStatuses[userId]?.status === 'pending') return;
    // Optimistic: flip to pending immediately so a double-click can't fire
    // twice; roll back if the request fails
    setConnectionStatuses(prev => ({
      ...prev,
      [userId]: { status: 'pending', isInitiator: true }
    }));
    try {
      await api.sendConnectionRequest(userId);
    } catch (err) {
      setConnectionStatuses(prev => ({ ...prev, [userId]: { status: 'none' } }));
      console.error('Failed to send connection request:', err);
      toast.error(err.message);
    }
  };

  const getActionButton = (golfer) => {
    const status = connectionStatuses[golfer.id];

    if (!status || status.status === 'none') {
      return (
        <button
          className="btn btn-primary btn-block"
          onClick={(e) => {
            e.preventDefault();
            handleConnect(golfer.id);
          }}
        >
          Connect
        </button>
      );
    }

    if (status.status === 'pending') {
      return (
        <button className="btn btn-secondary btn-block" disabled>
          {status.isInitiator ? 'Request Sent' : 'Pending'}
        </button>
      );
    }

    if (status.status === 'accepted') {
      return (
        <button className="btn btn-secondary btn-block" disabled>
          Connected
        </button>
      );
    }

    return null;
  };

  return (
    <div className="discover-page container">
      <div className="discover-header">
        <h1>Discover Golfers</h1>
        <p>Find playing partners near you</p>
      </div>

      <div className="discover-tabs">
        <span className="discover-tab active">Find Golfers</span>
        <Link to="/marketplace" className="discover-tab">Tee Time Marketplace</Link>
      </div>

      <div className="discover-controls card">
        <div className="search-bar">
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search by name, course, or city..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            className="btn btn-secondary filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="filters">
            <div className="filter-group">
              <label className="form-label">Search Radius</label>
              <select
                className="form-input form-select"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
              >
                <option value={5}>5 miles</option>
                <option value={10}>10 miles</option>
                <option value={25}>25 miles</option>
                <option value={50}>50 miles</option>
                <option value={100}>100 miles</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Handicap Range</label>
              <div className="handicap-inputs">
                <input
                  type="number"
                  className="form-input"
                  placeholder="Min"
                  value={handicapMin}
                  onChange={(e) => setHandicapMin(e.target.value)}
                  min="-10"
                  max="54"
                />
                <span>to</span>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Max"
                  value={handicapMax}
                  onChange={(e) => setHandicapMax(e.target.value)}
                  min="-10"
                  max="54"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {!user?.latitude && (
        <div className="alert alert-info">
          <p>Add your location to your profile to discover golfers near you.</p>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : golfers.length > 0 ? (
        <div className="golfers-grid">
          {golfers.map(golfer => (
            <UserCard
              key={golfer.id}
              user={golfer}
              actionButton={getActionButton(golfer)}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state card">
          <div className="empty-state-icon"><Icon name="search" size={24} /></div>
          <p className="empty-state-title">No golfers found</p>
          <p className="empty-state-text">
            {searchQuery
              ? 'Try a different search term or adjust your filters.'
              : 'Try expanding your search radius or check back later.'}
          </p>
        </div>
      )}
    </div>
  );
}

export default Discover;
